import React from 'react';

import { ListContactType } from '@bb/contacts';
import errorReporter from '@app/errorReporter';
import styled from 'styled-components';

import APIService, { ContactController } from 'api';
import Contact from '../Contact';
import { SearchBar } from '../SearchBar';
import message from '../Message';
import { AuthContext } from '../../contexts';
import Avatar from '../Avatar';

export type UserSearchType = 'agent' | 'contact' | 'brokerage' | 'adminContact';

interface UserSearchProps {
  autoFocus?: boolean;
  /**
   * Used to filter out users
   */
  filteredIds?: string[];
  customRenderer?: (item: any) => JSX.Element;
  filters?: Record<string, string[]>;
  filterSelf?: boolean;
  /**
   * Required when using contact search
   */
  listingId?: string;
  input?: string;
  onSelect: (result: any) => void;
  onClear?: () => void;
  multi?: boolean;
  selectedUser: any | undefined;
  width?: number;
  placeholder?: string;
  type: UserSearchType;
  /**
   * Excludes unverified users from brokerage search by default
   */
  includeUnverified?: boolean;
}

interface UserSearchState {
  searching: boolean;
  results: any[];
  value: string | undefined;
}

class UserSearch extends React.Component<UserSearchProps, UserSearchState> {
  declare context: React.ContextType<typeof AuthContext>;

  private _DEBOUNCE_TIMEOUT = 500;

  private _timeoutId?: NodeJS.Timeout;

  private inputRef = React.createRef<HTMLInputElement>();

  public constructor(props: UserSearchProps) {
    super(props);

    this.state = {
      searching: false,
      results: [],
      value: undefined,
    };
  }

  public componentDidMount() {
    const { autoFocus } = this.props;

    if (autoFocus) {
      setTimeout(() => this.inputRef.current.focus(), 1);
    }
  }

  private _handleSelectResult = (result: any) => {
    const { onSelect, multi } = this.props;

    if (!multi) {
      this.setState({ value: `${result.firstName} ${result.lastName}`.trim() });
    }

    this.setState({
      results: [],
    });
    onSelect(result);
    this.inputRef.current.blur();
  };

  public resultRenderer = (item: any, index: number, activeIndex: number) => {
    const { customRenderer, type } = this.props;

    if (customRenderer) return customRenderer(item);

    const onClick = () => {
      this._handleSelectResult(item);
    };

    return (
      <ContactListItem
        onClick={onClick}
        onMouseDown={ev => ev.preventDefault()}
        active={index === activeIndex}
      >
        <Contact
          contact={item}
          avatarSize={32}
          hoverable={false}
          disableClick
          showContactDetails={['contact', 'adminContact'].includes(type)}
        />
      </ContactListItem>
    );
  };

  private _agentSearch = async (input: string): Promise<any[]> => [];

  private _adminContactSearch = async (input: string) => {
    const { listingId } = this.props;
    const result = await ContactController.findClientsOwnedByListingAgents(
      listingId,
      input,
    );

    return result;
  };

  private _contactSearch = async (input: string) => {
    const { filteredIds, filters } = this.props;

    const {
      active: { contacts },
    } = await ContactController.searchContacts(
      input,
      ListContactType.Client,
      0,
      20,
      null,
      filters,
    );

    if (!filteredIds) return contacts;
    return contacts.filter(contact => !filteredIds.includes(contact.id));
  };

  private _brokerageSearch = async (input: string) => {
    const { filteredIds } = this.props;
    const {
      auth: {
        org: { id: orgId },
      },
    } = this.context;
    const { users } = await APIService.GET.search({
      name: input,
      orgId,
      page: 0,
      excludeInternalOrgs: true,
      includeUnverified: true,
    });

    if (!filteredIds) return users;
    return users.filter(user => !filteredIds.includes(user.id));
  };

  private _API_CALL_MAP: Record<
    UserSearchType,
    (input: string) => Promise<any[]>
  > = {
    agent: this._agentSearch,
    adminContact: this._adminContactSearch,
    contact: this._contactSearch,
    brokerage: this._brokerageSearch,
  };

  public handleSearch = (input: string) => {
    const { filterSelf, onClear, type } = this.props;
    const { auth } = this.context;

    try {
      if (this._timeoutId) {
        clearTimeout(this._timeoutId);
      }

      this.setState({ value: input, results: [] }, onClear);

      this._timeoutId = setTimeout(async () => {
        if (input) {
          this.setState({ searching: true });
          let result = await this._API_CALL_MAP[type](input);
          if (filterSelf)
            result = result.filter(({ id }) => id !== auth.id);
          this.setState({ results: result, searching: false });
        }
      }, this._DEBOUNCE_TIMEOUT);
    } catch (err) {
      message.error('Could not compelte search, please try again.');
      errorReporter.error(err);
      this.setState({ searching: false });
    }
  };

  public render() {
    const { results, searching, value } = this.state;

    const { selectedUser, width, placeholder } = this.props;

    return (
      <Container width={width}>
        <SearchBar
          // @ts-ignore
          ref={this.inputRef}
          allowClear
          onChange={this.handleSearch}
          placeholder={placeholder || 'Search within your brokerage'}
          resultData={results}
          customResultRenderer={this.resultRenderer}
          displayResults
          resultsPosition="absolute"
          loading={searching}
          value={value}
          prefix={
            selectedUser && !searching ? (
              <Avatar width={19} user={selectedUser} />
            ) : null
          }
          // onSelect={() => this._handleSelectResult()}
        />
      </Container>
    );
  }
}

UserSearch.contextType = AuthContext;

export default UserSearch;

const Container = styled.div<{ width?: number }>`
  ${({ width }) => (width ? `width: ${width}px;` : `width: 100%`)};
`;

const ContactListItem = styled.div<{ active?: boolean }>`
  width: 100%;
  padding: 3.5px 12px;
  &:hover {
    background-color: #f1f1f1;
    cursor: pointer;
  }
  ${({ active }) => (active ? 'background-color: #f1f1f1' : '')}
`;
